// client/src/components/Goals/GoalForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  Tooltip,
} from '@mui/material';

const PRESET_COLORS = [
  '#22c55e', '#3b82f6', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#f97316', '#ec4899',
];

/**
 * GoalForm — MUI Dialog for creating / editing a savings goal.
 * Props:
 *   open          — boolean
 *   onClose       — () => void
 *   onSave        — (goalData) => void
 *   initialData   — partial goal object when editing (optional)
 *   accounts      — array of Account objects (optional)
 *   categories    — array of Category objects (optional)
 */
const GoalForm = ({ open, onClose, onSave, initialData = null, accounts = [], categories = [] }) => {
  const isEdit = Boolean(initialData && initialData._id);

  const blank = {
    name: '',
    targetAmount: '',
    targetDate: '',
    color: '#22c55e',
    icon: '🎯',
    notes: '',
    linkMode: 'none', // 'none' | 'account' | 'category'
    linkedAccountId: '',
    linkedCategoryId: '',
  };

  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          name: initialData.name || '',
          targetAmount: initialData.targetAmount || '',
          targetDate: initialData.targetDate
            ? new Date(initialData.targetDate).toISOString().split('T')[0]
            : '',
          color: initialData.color || '#22c55e',
          icon: initialData.icon || '🎯',
          notes: initialData.notes || '',
          linkMode: initialData.linkedAccountId
            ? 'account'
            : initialData.linkedCategoryId
              ? 'category'
              : 'none',
          linkedAccountId: initialData.linkedAccountId || '',
          linkedCategoryId: initialData.linkedCategoryId || '',
        });
      } else {
        setForm(blank);
      }
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.targetAmount || Number(form.targetAmount) <= 0)
      errs.targetAmount = 'Target amount must be > 0';
    return errs;
  };

  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const payload = {
      name: form.name.trim(),
      targetAmount: Number(form.targetAmount),
      targetDate: form.targetDate || undefined,
      color: form.color,
      icon: form.icon || '🎯',
      notes: form.notes,
      linkedAccountId: form.linkMode === 'account' ? form.linkedAccountId || undefined : undefined,
      linkedCategoryId: form.linkMode === 'category' ? form.linkedCategoryId || undefined : undefined,
    };

    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Savings Goal' : 'New Savings Goal'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {/* Name */}
          <TextField
            fullWidth
            label="Goal name"
            value={form.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="e.g. Emergency fund, Vacation to Italy"
          />

          {/* Target amount */}
          <TextField
            fullWidth
            label="Target amount"
            type="number"
            value={form.targetAmount}
            onChange={handleChange('targetAmount')}
            error={!!errors.targetAmount}
            helperText={errors.targetAmount}
            inputProps={{ min: 0, step: 0.01 }}
          />

          {/* Target date */}
          <TextField
            fullWidth
            label="Target date (optional)"
            type="date"
            value={form.targetDate}
            onChange={handleChange('targetDate')}
            InputLabelProps={{ shrink: true }}
          />

          {/* Color swatches */}
          <Box>
            <Typography variant="body2" gutterBottom color="text.secondary">Color</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {PRESET_COLORS.map((c) => (
                <Tooltip title={c} key={c}>
                  <Box
                    onClick={() => setForm((f) => ({ ...f, color: c }))}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: c,
                      cursor: 'pointer',
                      border: form.color === c ? '3px solid' : '2px solid transparent',
                      borderColor: form.color === c ? 'text.primary' : 'transparent',
                      transition: 'transform 0.15s',
                      '&:hover': { transform: 'scale(1.2)' },
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>

          {/* Icon emoji input */}
          <TextField
            fullWidth
            label="Icon (emoji)"
            value={form.icon}
            onChange={handleChange('icon')}
            placeholder="🎯"
            inputProps={{ maxLength: 4 }}
            helperText="Paste or type an emoji"
          />

          {/* Link mode — mutually exclusive */}
          <Box>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
                Link goal progress to (optional)
              </FormLabel>
              <RadioGroup
                row
                value={form.linkMode}
                onChange={handleChange('linkMode')}
              >
                <FormControlLabel value="none" control={<Radio size="small" />} label="None (manual)" />
                <FormControlLabel value="account" control={<Radio size="small" />} label="Account" />
                <FormControlLabel value="category" control={<Radio size="small" />} label="Category" />
              </RadioGroup>
            </FormControl>

            {form.linkMode === 'account' && (
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Account</InputLabel>
                <Select
                  value={form.linkedAccountId}
                  label="Account"
                  onChange={handleChange('linkedAccountId')}
                >
                  <MenuItem value=""><em>Select account</em></MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc._id} value={acc._id}>
                      {acc.icon || '🏦'} {acc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {form.linkMode === 'category' && (
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.linkedCategoryId}
                  label="Category"
                  onChange={handleChange('linkedCategoryId')}
                >
                  <MenuItem value=""><em>Select category</em></MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id || cat._id} value={cat.id || cat._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cat.color || '#1976d2' }} />
                        {cat.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Notes */}
          <TextField
            fullWidth
            label="Notes (optional)"
            value={form.notes}
            onChange={handleChange('notes')}
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {isEdit ? 'Update Goal' : 'Create Goal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoalForm;
